import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Mail, Phone, MapPin, Send, ArrowUpRight } from "lucide-react";
import { personalInfo } from "../../data/mock";
import { toast } from "../../hooks/use-toast";
import emailjs from "@emailjs/browser";

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const ContactSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.15 });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
        },
        EMAILJS_PUBLIC_KEY
      );
      toast({
        title: "Message sent!",
        description: "Thanks for reaching out. I'll get back to you soon.",
      });
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error("EmailJS error:", error);
      toast({
        title: "Failed to send",
        description: "Something went wrong. Please try again or email me directly.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const contactDetails = [
    {
      icon: Mail,
      label: "Email",
      value: personalInfo.email,
      href: `mailto:${personalInfo.email}`,
    },
    {
      icon: Phone,
      label: "Phone",
      value: personalInfo.phone,
      href: `tel:${personalInfo.phone}`,
    },
    {
      icon: MapPin,
      label: "Location",
      value: personalInfo.location,
      href: null,
    },
  ];

  return (
    <section id="contact" className="py-24 sm:py-32 relative" ref={ref} aria-label="Contact Aditya Aman">
      {/* Background effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-[rgba(218,255,1,0.02)] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-sm font-semibold text-[rgb(218,255,1)] uppercase tracking-widest">
            Contact
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3 tracking-tight">
            Let's work{" "}
            <span className="text-[rgb(218,255,1)]">together</span>
          </h2>
          <p className="text-[rgb(161,161,170)] mt-4 max-w-lg">
            Have a project in mind or want to discuss an opportunity? Feel free
            to reach out.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 flex flex-col gap-4"
          >
            {contactDetails.map((detail) => (
              <div
                key={detail.label}
                className="group flex items-center gap-4 p-5 rounded-2xl bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] hover:border-[rgba(218,255,1,0.3)] transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-[rgba(218,255,1,0.1)] flex items-center justify-center flex-shrink-0">
                  <detail.icon size={20} className="text-[rgb(218,255,1)]" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-[rgb(161,161,170)] uppercase tracking-wider mb-0.5">
                    {detail.label}
                  </p>
                  {detail.href ? (
                    <a
                      href={detail.href}
                      className="text-sm font-medium text-white hover:text-[rgb(218,255,1)] transition-colors truncate block"
                    >
                      {detail.value}
                    </a>
                  ) : (
                    <p className="text-sm font-medium text-white">
                      {detail.value}
                    </p>
                  )}
                </div>
                {detail.href && (
                  <ArrowUpRight
                    size={16}
                    className="ml-auto text-[rgb(63,63,63)] group-hover:text-[rgb(218,255,1)] transition-colors flex-shrink-0"
                  />
                )}
              </div>
            ))}

            {/* Hire Me CTA */}
            <div className="mt-4 p-6 rounded-2xl bg-gradient-to-br from-[rgba(218,255,1,0.08)] to-[rgba(218,255,1,0.02)] border border-[rgba(218,255,1,0.15)]">
              <p className="inline-flex items-center gap-2 text-xs font-semibold text-[rgb(34,197,94)] mb-2">
                <span className="w-2 h-2 rounded-full bg-[rgb(34,197,94)] animate-pulse" />
                Currently open to full-time opportunities. Immediate joiner.
              </p>
              <p className="text-sm text-[rgb(218,218,218)] mb-3">
                Open to freelance projects and full-time roles.
              </p>
              <a
                href={personalInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[rgb(218,255,1)] hover:underline"
              >
                Hire Me
                <ArrowUpRight size={14} />
              </a>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-3"
          >
            <form
              onSubmit={handleSubmit}
              className="p-6 sm:p-8 rounded-2xl bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)]"
            >
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-[rgb(161,161,170)] uppercase tracking-wider mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="w-full px-4 py-3 rounded-xl bg-[rgb(38,40,42)] border-2 border-[rgb(63,63,63)] text-white text-sm placeholder:text-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(218,255,1)] focus:shadow-[0_0_0_4px_rgba(218,255,1,0.1)] transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[rgb(161,161,170)] uppercase tracking-wider mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 rounded-xl bg-[rgb(38,40,42)] border-2 border-[rgb(63,63,63)] text-white text-sm placeholder:text-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(218,255,1)] focus:shadow-[0_0_0_4px_rgba(218,255,1,0.1)] transition-all duration-200"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs font-medium text-[rgb(161,161,170)] uppercase tracking-wider mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="What's this about?"
                  className="w-full px-4 py-3 rounded-xl bg-[rgb(38,40,42)] border-2 border-[rgb(63,63,63)] text-white text-sm placeholder:text-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(218,255,1)] focus:shadow-[0_0_0_4px_rgba(218,255,1,0.1)] transition-all duration-200"
                />
              </div>

              <div className="mb-6">
                <label className="block text-xs font-medium text-[rgb(161,161,170)] uppercase tracking-wider mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell me about your project..."
                  className="w-full px-4 py-3 rounded-xl bg-[rgb(38,40,42)] border-2 border-[rgb(63,63,63)] text-white text-sm placeholder:text-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(218,255,1)] focus:shadow-[0_0_0_4px_rgba(218,255,1,0.1)] transition-all duration-200 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] font-semibold rounded-xl hover:bg-[rgb(166,190,21)] hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(218,255,1,0.3)] disabled:opacity-60 disabled:hover:translate-y-0 transition-all duration-200"
              >
                {sending ? (
                  <>
                    <span className="w-4 h-4 border-2 border-[rgb(17,17,19)] border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
